import { createApi } from '@reduxjs/toolkit/query/react';

import {
  IBlockUserRequest,
  IChangePasswordRequest,
  IChangePasswordResponse,
  IChangePhonePushStatusRequest,
  IChangePhonePushStatusResponse,
  IDeleteUserRequest,
  ILoginResponse,
  ISendSmsRequest,
  ISendSmsResponse,
  ISmsVerifyRequest,
  ISmsVerifyResponse,
  IUserData,
} from '@/interfaces/user';

import { baseQueryWithReauth } from './baseQuery';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getUserProfile: builder.query<IUserData, void>({
      query: () => ({
        url: 'auth/user-profile',
        method: 'GET',
      }),
    }),
    changePassword: builder.mutation<
      IChangePasswordResponse,
      IChangePasswordRequest
    >({
      query: (data) => ({
        url: 'auth/change-password',
        method: 'POST',
        body: data,
      }),
    }),
    sendSms: builder.mutation<ISendSmsResponse, ISendSmsRequest>({
      query: (data) => ({
        url: 'user/change-phone/request',
        method: 'POST',
        body: data,
      }),
    }),
    smsVerify: builder.mutation<ISmsVerifyResponse, ISmsVerifyRequest>({
      query: (data) => ({
        url: 'user/change-phone/confirm',
        method: 'POST',
        body: data,
      }),
    }),
    changePhonePushStatus: builder.query<
      IChangePhonePushStatusResponse,
      IChangePhonePushStatusRequest
    >({
      query: (data) => ({
        url: 'user/change-phone/push-status',
        method: 'GET',
        params: { auth_req_id: data.auth_req_id },
      }),
    }),
    changeEmail: builder.mutation<{ message: string }, { email: string }>({
      query: (data) => ({
        url: 'auth/change-email',
        method: 'POST',
        body: data,
      }),
    }),
    confirmEmailChange: builder.mutation<
      { message: string },
      { token: string }
    >({
      query: ({ token }) => ({
        url: `auth/confirm-email-change/${token}`,
        method: 'GET',
      }),
    }),
    blockUser: builder.mutation<IBlockUserRequest, void>({
      query: () => ({
        url: 'user/block',
        method: 'POST',
      }),
    }),
    deleteUser: builder.mutation<IDeleteUserRequest, void>({
      query: () => ({
        url: 'user/delete',
        method: 'DELETE',
      }),
    }),
    loginIntoCustomer: builder.mutation<ILoginResponse, { customerId: number }>(
      {
        query: (data) => ({
          url: `user/impersonate/${data.customerId}`,
          method: 'POST',
        }),
      },
    ),
    returnIntoOperator: builder.mutation<ILoginResponse, void>({
      query: () => ({
        url: 'user/leaveImpersonation',
        method: 'POST',
      }),

      transformResponse(res: ILoginResponse) {
        const accessToken = res.access_token;

        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
        }

        return res;
      },
    }),
  }),
});

export const {
  useLazyGetUserProfileQuery,
  useChangePasswordMutation,
  useSendSmsMutation,
  useSmsVerifyMutation,
  useLazyChangePhonePushStatusQuery,
  useBlockUserMutation,
  useDeleteUserMutation,
  useLoginIntoCustomerMutation,
  useReturnIntoOperatorMutation,
  useChangeEmailMutation,
  useConfirmEmailChangeMutation,
} = userApi;
